/**
 * Lightweight fuzzy matching — no external dependency.
 *
 * `fuzzyScore` returns a relevance score for a single field against a query:
 *   - exact match .......... 100
 *   - prefix match ......... 90
 *   - substring match ...... 80
 *   - subsequence match .... 1..60 (denser / earlier matches score higher)
 *   - no match ............. 0
 *
 * A subsequence match means every character of the query appears in order in
 * the target (e.g. "acme" matches "A Cool Media Enterprise"), which is what
 * makes the search tolerant of typos and skipped words.
 */
export function fuzzyScore(text: string | null | undefined, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const t = (text ?? "").toLowerCase();
  if (!t) return 0;

  if (t === q) return 100;
  if (t.startsWith(q)) return 90;
  if (t.includes(q)) return 80;

  // Subsequence: walk the target, consuming query chars in order.
  let qi = 0;
  let firstMatch = -1;
  let lastMatch = -1;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      if (firstMatch === -1) firstMatch = ti;
      lastMatch = ti;
      qi++;
    }
  }
  if (qi < q.length) return 0; // not all query chars found in order

  // Reward compact matches that start early in the string.
  const span = lastMatch - firstMatch + 1;
  const density = q.length / span; // 0..1
  const earliness = 1 - firstMatch / t.length; // 0..1
  return Math.max(1, Math.round(60 * (0.7 * density + 0.3 * earliness)));
}

/**
 * Best fuzzy score across several fields (e.g. name, location, contact).
 */
export function fuzzyScoreFields(
  fields: (string | null | undefined)[],
  query: string,
): number {
  let best = 0;
  for (const field of fields) {
    const score = fuzzyScore(field, query);
    if (score > best) best = score;
  }
  return best;
}
